import client, { transformKeys } from './client';
import { toHealthConfigBody } from '../utils/healthScore';
import type {
  RequestListResponse,
  RequestDetail,
  FilterOption,
  FilterOptions,
  StatsSummary,
  StatsByCategory,
  TimeSeriesPoint,
  RequestFilters,
  ActiveUsersSummary,
  DauTimeSeriesPoint,
  DauByOrgTimeSeriesPoint,
  WauTimeSeriesPoint,
  ActivityByOrg,
  ActivityByEventType,
  HealthConfig,
  HealthConfigState,
  OrgHealthDetail,
  OrgHealthScore,
  OrgListEntry,
  UserRetention,
  CSOnboardingStatus,
  UserRole,
  UserOnboardingListResponse,
  UsageReportData,
  GenerateEmailResponse,
  EditEmailResponse,
  HoursSavedSummary,
  WeeklyTLDRResponse,
  EditTLDRResponse,
  SecurityAssessmentRequest,
  SecurityAssessmentResponse,
  AcceptRiskRequest,
  DocumentAnalysisResponse,
  ChatThread,
  ListThreadsResponse,
  SendMessageResponse,
  ChartConfig,
  AgentFilters,
  AgentSummary,
  AgentConversationListResponse,
  AgentConversationDetail,
} from '../types';

// Build query params from filters
function buildFilterParams(
  filters: Partial<RequestFilters>,
  excludeOrgIds?: string[],
  excludeUserIds?: string[]
): Record<string, string | string[]> {
  const params: Record<string, string | string[]> = {};

  if (filters.startDate) {
    params.start_date = filters.startDate.toISOString();
  }
  if (filters.endDate) {
    params.end_date = filters.endDate.toISOString();
  }
  if (filters.envs && filters.envs.length > 0) {
    params.envs = filters.envs;
  }
  if (filters.requestTypes && filters.requestTypes.length > 0) {
    params.request_types = filters.requestTypes;
  }
  if (filters.endpoints && filters.endpoints.length > 0) {
    params.endpoints = filters.endpoints;
  }
  if (filters.orgIds && filters.orgIds.length > 0) {
    params.org_ids = filters.orgIds;
  }
  if (filters.userIds && filters.userIds.length > 0) {
    params.user_ids = filters.userIds;
  }
  if (filters.tags && filters.tags.length > 0) {
    params.tags = filters.tags;
  }
  if (filters.search) {
    params.search = filters.search;
  }
  if (filters.successOnly) {
    params.success_only = 'true';
  }
  if (filters.feedbackTypes && filters.feedbackTypes.length > 0) {
    params.feedback_types = filters.feedbackTypes;
  }
  if (filters.checklistOnly) {
    params.checklist_only = 'true';
  }
  if (filters.surfaces && filters.surfaces.length > 0) {
    params.surfaces = filters.surfaces;
  }
  if (filters.agentId) {
    params.agent_id = filters.agentId;
  }
  if (filters.agentNames && filters.agentNames.length > 0) {
    params.agent_names = filters.agentNames;
  }
  if (filters.excludeScheduled) {
    params.exclude_scheduled = 'true';
  }
  if (excludeOrgIds && excludeOrgIds.length > 0) {
    params.exclude_org_ids = excludeOrgIds;
  }
  if (excludeUserIds && excludeUserIds.length > 0) {
    params.exclude_user_ids = excludeUserIds;
  }

  return params;
}

export async function listRequests(
  startDate: Date | null = null,
  endDate: Date | null = null,
  page: number = 1,
  pageSize: number = 50,
  sortOrder: 'asc' | 'desc' = 'desc',
  filters: Partial<RequestFilters> = {},
  excludeOrgIds?: string[],
  excludeUserIds?: string[]
): Promise<RequestListResponse> {
  const params: Record<string, string | string[]> = {
    page: page.toString(),
    page_size: pageSize.toString(),
    sort_order: sortOrder,
    ...buildFilterParams(filters, excludeOrgIds, excludeUserIds),
  };

  if (startDate) {
    params.start_date = startDate.toISOString();
  }
  if (endDate) {
    params.end_date = endDate.toISOString();
  }

  const response = await client.get('/requests', { params });
  return transformKeys(response.data) as RequestListResponse;
}

export async function getRequestDetail(traceId: string): Promise<RequestDetail> {
  const response = await client.get(`/requests/${traceId}`);
  return transformKeys(response.data) as RequestDetail;
}

export async function getQueryAnswer(traceId: string): Promise<string | null> {
  try {
    const response = await client.get(`/requests/${traceId}/answer`);
    return response.data.answer;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

export async function getFilterOptions(): Promise<FilterOptions> {
  const response = await client.get('/filters/options');
  return transformKeys(response.data) as FilterOptions;
}

// Identity + dashboard role of the signed-in user. This is the frontend's gate: a 403
// means the account authenticated against Descope but carries no `product:*` scope.
export interface Me {
  name: string;
  email: string;
  organization?: string;
  role: UserRole;
  isAdmin: boolean;
  scopes: string[];
}

export async function fetchMe(): Promise<Me> {
  const response = await client.get('/me');
  return transformKeys(response.data) as Me;
}

export async function getStatsSummary(
  filters: Partial<RequestFilters> = {},
  excludeOrgIds?: string[],
  excludeUserIds?: string[]
): Promise<StatsSummary> {
  const params = buildFilterParams(filters, excludeOrgIds, excludeUserIds);
  const response = await client.get('/stats/summary', { params });
  return transformKeys(response.data) as StatsSummary;
}

export async function getStatsByOrg(
  filters: Partial<RequestFilters> = {},
  limit: number = 20,
  excludeOrgIds?: string[],
  excludeUserIds?: string[]
): Promise<StatsByCategory[]> {
  const params = {
    ...buildFilterParams(filters, excludeOrgIds, excludeUserIds),
    limit: limit.toString(),
  };
  const response = await client.get('/stats/by-org', { params });
  return transformKeys(response.data) as StatsByCategory[];
}

export async function getStatsByUser(
  filters: Partial<RequestFilters> = {},
  limit: number = 20,
  excludeOrgIds?: string[],
  excludeUserIds?: string[]
): Promise<StatsByCategory[]> {
  const params = {
    ...buildFilterParams(filters, excludeOrgIds, excludeUserIds),
    limit: limit.toString(),
  };
  const response = await client.get('/stats/by-user', { params });
  return transformKeys(response.data) as StatsByCategory[];
}

export async function getStatsByType(
  filters: Partial<RequestFilters> = {},
  excludeOrgIds?: string[],
  excludeUserIds?: string[]
): Promise<StatsByCategory[]> {
  const params = buildFilterParams(filters, excludeOrgIds, excludeUserIds);
  const response = await client.get('/stats/by-type', { params });
  return transformKeys(response.data) as StatsByCategory[];
}

export async function getTimeseries(
  filters: Partial<RequestFilters> = {},
  granularity: 'hour' | 'day' | 'week' | 'month' = 'day',
  excludeOrgIds?: string[],
  excludeUserIds?: string[]
): Promise<TimeSeriesPoint[]> {
  const params = {
    ...buildFilterParams(filters, excludeOrgIds, excludeUserIds),
    granularity,
  };
  const response = await client.get('/stats/timeseries', { params });
  return transformKeys(response.data) as TimeSeriesPoint[];
}

// Metrics API functions for activity log analytics

export async function getMetricsOrganizations(): Promise<FilterOption[]> {
  const response = await client.get('/metrics/organizations');
  return transformKeys(response.data) as FilterOption[];
}

export async function getMetricsUsers(): Promise<FilterOption[]> {
  const response = await client.get('/metrics/users');
  return transformKeys(response.data) as FilterOption[];
}

export async function getActiveUsersSummary(
  startDate?: Date,
  endDate?: Date,
  orgIds?: string[],
  excludeOrgIds?: string[],
  excludeUserIds?: string[]
): Promise<ActiveUsersSummary> {
  const params: Record<string, string | string[]> = {};
  if (startDate) {
    params.start_date = startDate.toISOString();
  }
  if (endDate) {
    params.end_date = endDate.toISOString();
  }
  if (orgIds && orgIds.length > 0) {
    params.org_ids = orgIds;
  }
  if (excludeOrgIds && excludeOrgIds.length > 0) {
    params.exclude_org_ids = excludeOrgIds;
  }
  if (excludeUserIds && excludeUserIds.length > 0) {
    params.exclude_user_ids = excludeUserIds;
  }
  const response = await client.get('/metrics/summary', { params });
  return transformKeys(response.data) as ActiveUsersSummary;
}

export async function getDauTimeseries(
  startDate?: Date,
  endDate?: Date,
  orgIds?: string[],
  excludeOrgIds?: string[],
  excludeUserIds?: string[]
): Promise<DauTimeSeriesPoint[]> {
  const params: Record<string, string | string[]> = {};
  if (startDate) {
    params.start_date = startDate.toISOString();
  }
  if (endDate) {
    params.end_date = endDate.toISOString();
  }
  if (orgIds && orgIds.length > 0) {
    params.org_ids = orgIds;
  }
  if (excludeOrgIds && excludeOrgIds.length > 0) {
    params.exclude_org_ids = excludeOrgIds;
  }
  if (excludeUserIds && excludeUserIds.length > 0) {
    params.exclude_user_ids = excludeUserIds;
  }
  const response = await client.get('/metrics/dau-timeseries', { params });
  return transformKeys(response.data) as DauTimeSeriesPoint[];
}

export async function getDauByOrgTimeseries(
  startDate?: Date,
  endDate?: Date,
  orgIds?: string[],
  excludeOrgIds?: string[],
  excludeUserIds?: string[]
): Promise<DauByOrgTimeSeriesPoint[]> {
  const params: Record<string, string | string[]> = {};
  if (startDate) {
    params.start_date = startDate.toISOString();
  }
  if (endDate) {
    params.end_date = endDate.toISOString();
  }
  if (orgIds && orgIds.length > 0) {
    params.org_ids = orgIds;
  }
  if (excludeOrgIds && excludeOrgIds.length > 0) {
    params.exclude_org_ids = excludeOrgIds;
  }
  if (excludeUserIds && excludeUserIds.length > 0) {
    params.exclude_user_ids = excludeUserIds;
  }
  const response = await client.get('/metrics/dau-by-org-timeseries', { params });
  return transformKeys(response.data) as DauByOrgTimeSeriesPoint[];
}

export async function getWauTimeseries(
  startDate?: Date,
  endDate?: Date,
  orgIds?: string[],
  excludeOrgIds?: string[],
  excludeUserIds?: string[]
): Promise<WauTimeSeriesPoint[]> {
  const params: Record<string, string | string[]> = {};
  if (startDate) {
    params.start_date = startDate.toISOString();
  }
  if (endDate) {
    params.end_date = endDate.toISOString();
  }
  if (orgIds && orgIds.length > 0) {
    params.org_ids = orgIds;
  }
  if (excludeOrgIds && excludeOrgIds.length > 0) {
    params.exclude_org_ids = excludeOrgIds;
  }
  if (excludeUserIds && excludeUserIds.length > 0) {
    params.exclude_user_ids = excludeUserIds;
  }
  const response = await client.get('/metrics/wau-timeseries', { params });
  return transformKeys(response.data) as WauTimeSeriesPoint[];
}

export async function getActivityByOrg(
  startDate?: Date,
  endDate?: Date,
  limit: number = 20,
  excludeOrgIds?: string[],
  excludeUserIds?: string[]
): Promise<ActivityByOrg[]> {
  const params: Record<string, string | string[]> = { limit: limit.toString() };
  if (startDate) {
    params.start_date = startDate.toISOString();
  }
  if (endDate) {
    params.end_date = endDate.toISOString();
  }
  if (excludeOrgIds && excludeOrgIds.length > 0) {
    params.exclude_org_ids = excludeOrgIds;
  }
  if (excludeUserIds && excludeUserIds.length > 0) {
    params.exclude_user_ids = excludeUserIds;
  }
  const response = await client.get('/metrics/by-org', { params });
  return transformKeys(response.data) as ActivityByOrg[];
}

export async function updateOrgCompanySize(
  orgId: string,
  companySize: string | null,
): Promise<void> {
  await client.put(`/metrics/organizations/${orgId}/company-size`, {
    company_size: companySize,
  });
}

export async function getOrgList(
  startDate?: Date,
  endDate?: Date,
  excludeOrgIds?: string[],
  excludeUserIds?: string[]
): Promise<OrgListEntry[]> {
  const params: Record<string, string | string[]> = {};
  if (startDate) {
    params.start_date = startDate.toISOString();
  }
  if (endDate) {
    params.end_date = endDate.toISOString();
  }
  if (excludeOrgIds && excludeOrgIds.length > 0) {
    params.exclude_org_ids = excludeOrgIds;
  }
  if (excludeUserIds && excludeUserIds.length > 0) {
    params.exclude_user_ids = excludeUserIds;
  }
  const response = await client.get('/metrics/org-list', { params });
  return transformKeys(response.data) as OrgListEntry[];
}

/**
 * Company health score per org.
 *
 * Takes no date range on purpose: the score is always the fixed rolling 28-day window
 * (see the backend's `constants/health_score.py`). Exclusions are still passed so the
 * health column agrees with the counts beside it. Returned worst-first.
 */
export async function getOrgHealth(
  excludeOrgIds?: string[],
  excludeUserIds?: string[]
): Promise<OrgHealthScore[]> {
  const response = await client.get('/metrics/org-health', {
    params: exclusionParams(excludeOrgIds, excludeUserIds),
  });
  return transformKeys(response.data) as OrgHealthScore[];
}

function exclusionParams(excludeOrgIds?: string[], excludeUserIds?: string[]) {
  const params: Record<string, string | string[]> = {};
  if (excludeOrgIds && excludeOrgIds.length > 0) {
    params.exclude_org_ids = excludeOrgIds;
  }
  if (excludeUserIds && excludeUserIds.length > 0) {
    params.exclude_user_ids = excludeUserIds;
  }
  return params;
}

/** Why one org's health score is what it is. Pass the same exclusions as `getOrgHealth`. */
export async function getOrgHealthDetail(
  orgId: string,
  excludeOrgIds?: string[],
  excludeUserIds?: string[]
): Promise<OrgHealthDetail> {
  const response = await client.get(`/metrics/org-health/${orgId}`, {
    params: exclusionParams(excludeOrgIds, excludeUserIds),
  });
  return transformKeys(response.data) as OrgHealthDetail;
}

/** Score every org with a draft config without saving it. */
export async function previewOrgHealth(
  config: HealthConfig,
  excludeOrgIds?: string[],
  excludeUserIds?: string[]
): Promise<OrgHealthScore[]> {
  const response = await client.post('/metrics/org-health/preview', {
    config: toHealthConfigBody(config),
    exclude_org_ids: excludeOrgIds && excludeOrgIds.length > 0 ? excludeOrgIds : null,
    exclude_user_ids: excludeUserIds && excludeUserIds.length > 0 ? excludeUserIds : null,
  });
  return transformKeys(response.data) as OrgHealthScore[];
}

/** The weights, targets and tier cutoffs the health score is currently using. */
export async function getHealthConfig(): Promise<HealthConfigState> {
  const response = await client.get('/metrics/health-config');
  return transformKeys(response.data) as HealthConfigState;
}

/**
 * Save a new config, company-wide and immediately.
 *
 * The backend rejects an invalid one with a 422 (weights not summing to 1.0, Watch at or
 * above Healthy, a non-positive target) — callers should pre-validate so the user sees
 * the problem before submitting.
 */
export async function updateHealthConfig(config: HealthConfig): Promise<HealthConfigState> {
  const response = await client.put('/metrics/health-config', toHealthConfigBody(config));
  return transformKeys(response.data) as HealthConfigState;
}

/** Drop the saved config so the score falls back to the defaults. */
export async function resetHealthConfig(): Promise<HealthConfigState> {
  const response = await client.delete('/metrics/health-config');
  return transformKeys(response.data) as HealthConfigState;
}

export async function getActivityByEventType(
  startDate?: Date,
  endDate?: Date,
  orgIds?: string[],
  excludeOrgIds?: string[],
  excludeUserIds?: string[]
): Promise<ActivityByEventType[]> {
  const params: Record<string, string | string[]> = {};
  if (startDate) {
    params.start_date = startDate.toISOString();
  }
  if (endDate) {
    params.end_date = endDate.toISOString();
  }
  if (orgIds && orgIds.length > 0) {
    params.org_ids = orgIds;
  }
  if (excludeOrgIds && excludeOrgIds.length > 0) {
    params.exclude_org_ids = excludeOrgIds;
  }
  if (excludeUserIds && excludeUserIds.length > 0) {
    params.exclude_user_ids = excludeUserIds;
  }
  const response = await client.get('/metrics/by-event-type', { params });
  return transformKeys(response.data) as ActivityByEventType[];
}

export async function getUserRetention(
  startDate?: Date,
  endDate?: Date,
  orgIds?: string[],
  excludeOrgIds?: string[],
  excludeUserIds?: string[]
): Promise<UserRetention> {
  const params: Record<string, string | string[]> = {};
  if (startDate) {
    params.start_date = startDate.toISOString();
  }
  if (endDate) {
    params.end_date = endDate.toISOString();
  }
  if (orgIds && orgIds.length > 0) {
    params.org_ids = orgIds;
  }
  if (excludeOrgIds && excludeOrgIds.length > 0) {
    params.exclude_org_ids = excludeOrgIds;
  }
  if (excludeUserIds && excludeUserIds.length > 0) {
    params.exclude_user_ids = excludeUserIds;
  }
  const response = await client.get('/metrics/retention', { params });
  return transformKeys(response.data) as UserRetention;
}

export async function getHoursSavedSummary(
  startDate?: Date,
  endDate?: Date,
  orgIds?: string[],
  excludeOrgIds?: string[],
  excludeUserIds?: string[]
): Promise<HoursSavedSummary> {
  const params: Record<string, string | string[]> = {};
  if (startDate) {
    params.start_date = startDate.toISOString();
  }
  if (endDate) {
    params.end_date = endDate.toISOString();
  }
  if (orgIds && orgIds.length > 0) {
    params.org_ids = orgIds;
  }
  if (excludeOrgIds && excludeOrgIds.length > 0) {
    params.exclude_org_ids = excludeOrgIds;
  }
  if (excludeUserIds && excludeUserIds.length > 0) {
    params.exclude_user_ids = excludeUserIds;
  }
  const response = await client.get('/metrics/hours-saved', { params });
  return transformKeys(response.data) as HoursSavedSummary;
}

// Onboarding API functions
//
// Backend only handles org-scope filtering (orgs / excludes / include_inactive)
// + sort. All column-level filtering, summary aggregation, and CSV export are
// done client-side from the returned user list.

export interface OnboardingFilters {
  orgIds?: string[];
  excludeOrgIds?: string[];
  excludeUserIds?: string[];
  includeInactive?: boolean;
}

export async function getOnboardingUsers(
  filters: OnboardingFilters = {},
  sortBy: string = 'created_at',
  sortOrder: 'asc' | 'desc' = 'desc'
): Promise<UserOnboardingListResponse> {
  const params: Record<string, string | string[]> = {
    sort_by: sortBy,
    sort_order: sortOrder,
  };
  if (filters.orgIds?.length) params.org_ids = filters.orgIds;
  if (filters.excludeOrgIds?.length) params.exclude_org_ids = filters.excludeOrgIds;
  if (filters.excludeUserIds?.length) params.exclude_user_ids = filters.excludeUserIds;
  if (filters.includeInactive) params.include_inactive = 'true';
  const response = await client.get('/onboarding/users', { params });
  return transformKeys(response.data) as UserOnboardingListResponse;
}

export async function updateCSOnboardingStatus(
  userId: string,
  status: CSOnboardingStatus
): Promise<void> {
  await client.put(`/onboarding/users/${userId}/cs-status`, { status });
}

export async function updateCSTags(userId: string, tags: string[]): Promise<void> {
  await client.put(`/onboarding/users/${userId}/cs-tags`, { tags });
}

/**
 * Fetch the per-user CS context payload — used by the Onboarding tab's FU
 * button to pre-fetch before navigating to chat. We intentionally return the
 * raw snake_case body (not transformKeys'd) so it can be forwarded to the
 * chat send endpoint as `cs_context_seed` without a camel→snake round-trip.
 */
export async function fetchUserCSContextRaw(
  userId: string
): Promise<Record<string, unknown>> {
  const response = await client.get(`/onboarding/users/${userId}/cs-context`);
  return response.data as Record<string, unknown>;
}

export async function listCSTags(): Promise<string[]> {
  const response = await client.get('/onboarding/cs-tags');
  return response.data as string[];
}

// Usage Report API

export async function getUsageReportData(
  orgId: string,
  startDate?: Date,
  endDate?: Date,
  excludeUserIds?: string[]
): Promise<UsageReportData> {
  const params: Record<string, string | string[]> = {};
  if (startDate) {
    params.start_date = startDate.toISOString();
  }
  if (endDate) {
    params.end_date = endDate.toISOString();
  }
  if (excludeUserIds && excludeUserIds.length > 0) {
    params.exclude_user_ids = excludeUserIds;
  }
  const response = await client.get(`/metrics/usage-report/${orgId}`, { params });
  return transformKeys(response.data) as UsageReportData;
}

// AI Email Generation API

export async function generateCustomerEmail(
  orgId: string,
  startDate?: Date,
  endDate?: Date,
  customInstructions?: string
): Promise<GenerateEmailResponse> {
  const body: Record<string, string | undefined> = {};
  if (startDate) {
    body.start_date = startDate.toISOString();
  }
  if (endDate) {
    body.end_date = endDate.toISOString();
  }
  if (customInstructions) {
    body.custom_instructions = customInstructions;
  }
  const response = await client.post(`/metrics/generate-email/${orgId}`, body);
  return transformKeys(response.data) as GenerateEmailResponse;
}

export async function editCustomerEmail(
  sessionId: string,
  editInstructions: string
): Promise<EditEmailResponse> {
  const response = await client.post('/metrics/edit-email', {
    session_id: sessionId,
    edit_instructions: editInstructions,
  });
  return transformKeys(response.data) as EditEmailResponse;
}

// Weekly TLDR API

export async function generateWeeklyTLDR(
  orgId: string,
  startDate?: Date,
  endDate?: Date
): Promise<WeeklyTLDRResponse> {
  const body: Record<string, string | undefined> = {};
  if (startDate) {
    body.start_date = startDate.toISOString();
  }
  if (endDate) {
    body.end_date = endDate.toISOString();
  }
  const response = await client.post(`/metrics/weekly-tldr/${orgId}`, body);
  return transformKeys(response.data) as WeeklyTLDRResponse;
}

export async function editWeeklyTLDR(
  sessionId: string,
  editInstructions: string
): Promise<EditTLDRResponse> {
  const response = await client.post('/metrics/edit-tldr', {
    session_id: sessionId,
    edit_instructions: editInstructions,
  });
  return transformKeys(response.data) as EditTLDRResponse;
}

// Security Assessment API

export async function generateSecurityAssessment(
  request: SecurityAssessmentRequest
): Promise<SecurityAssessmentResponse> {
  const body: any = {
    org_id: request.orgId,
    period_days: request.periodDays,
    include_sensitive_analysis: request.includeSensitiveAnalysis,
  };
  if (request.sensitivityCategories) {
    body.sensitivity_categories = request.sensitivityCategories;
  }
  if (request.excludeOrgIds) {
    body.exclude_org_ids = request.excludeOrgIds;
  }
  if (request.excludeUserIds) {
    body.exclude_user_ids = request.excludeUserIds;
  }
  const response = await client.post('/security/assessment', body);
  return transformKeys(response.data) as SecurityAssessmentResponse;
}

export async function acceptSecurityRisk(
  request: AcceptRiskRequest
): Promise<{ success: boolean }> {
  const response = await client.post('/security/accept-risk', {
    org_id: request.orgId,
    risk_id: request.riskId,
    risk_title: request.riskTitle,
    accepted_by: request.acceptedBy,
    reason: request.reason,
  });
  return transformKeys(response.data) as { success: boolean };
}

export async function analyzeDocumentCSV(
  file: File,
  orgId: string
): Promise<DocumentAnalysisResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('org_id', orgId);

  const response = await client.post('/security/analyze-documents', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return transformKeys(response.data) as DocumentAnalysisResponse;
}

// LogRocket Highlights API

export interface LogRocketScreenshot {
  videoTime: number;
  url: string;
}

export interface LogRocketSession {
  recordingId: string;
  sessionUrl: string;
  highlights: string;
  screenshots: LogRocketScreenshot[];
  minVideoTime: number;
  maxVideoTime: number;
}

export interface LogRocketHighlightsResponse {
  status: 'ready' | 'pending' | 'failed' | 'disabled';
  requestId?: string;
  overallHighlights?: string;
  sessions?: LogRocketSession[];
  error?: string;
}

export async function getLogRocketHighlights(
  userId: string,
  timestamp: number
): Promise<LogRocketHighlightsResponse> {
  const response = await client.get('/logrocket/highlights', {
    params: { user_id: userId, timestamp },
  });
  return transformKeys(response.data) as LogRocketHighlightsResponse;
}

export async function checkLogRocketHighlightsStatus(
  requestId: string
): Promise<LogRocketHighlightsResponse> {
  const response = await client.get(`/logrocket/highlights/status/${requestId}`);
  return transformKeys(response.data) as LogRocketHighlightsResponse;
}

// Chat with Usage API

export async function sendChatMessage(
  message: string,
  threadId?: string
): Promise<SendMessageResponse> {
  const body: Record<string, string | undefined> = { message };
  if (threadId) {
    body.thread_id = threadId;
  }
  const response = await client.post('/chat/send', body);
  return transformKeys(response.data) as SendMessageResponse;
}

export async function listChatThreads(
  limit: number = 20,
  includeArchived: boolean = false,
  includePublic: boolean = true
): Promise<ListThreadsResponse> {
  const response = await client.get('/chat/threads', {
    params: { limit, include_archived: includeArchived, include_public: includePublic },
  });
  return transformKeys(response.data) as ListThreadsResponse;
}

export async function getChatThread(threadId: string): Promise<ChatThread> {
  const response = await client.get(`/chat/threads/${threadId}`);
  return transformKeys(response.data) as ChatThread;
}

export async function deleteChatThread(threadId: string): Promise<void> {
  await client.delete(`/chat/threads/${threadId}`);
}

export async function toggleThreadVisibility(
  threadId: string,
  isPublic: boolean
): Promise<{ threadId: string; isPublic: boolean }> {
  const response = await client.patch(`/chat/threads/${threadId}/visibility`, {
    is_public: isPublic,
  });
  return transformKeys(response.data) as { threadId: string; isPublic: boolean };
}

// SSE Event types for streaming chat
export interface SSEEvent {
  type: string;
  data?: Record<string, unknown>;
}

export interface ToolResultData {
  rowCount: number;
  columns: string[];
  executionTimeMs: number;
  truncated: boolean;
  rows?: Record<string, unknown>[];
  chart?: ChartConfig;
}

export interface ChatStreamCallbacks {
  onConnected?: (threadId: string, isNewThread: boolean) => void;
  onStatus?: (message: string) => void;
  onToolStart?: (tool: string, input: Record<string, unknown>) => void;
  onToolResult?: (tool: string, data: ToolResultData) => void;
  onToolError?: (tool: string, error: string) => void;
  onTextDelta?: (content: string) => void;
  onMessageSaved?: (messageId: string, role: string) => void;
  onThreadTitle?: (title: string) => void;
  onDone?: (threadId: string, userMessageId: string, assistantMessageId: string, toolMessageIds: string[]) => void;
  onError?: (error: string, recoverable: boolean) => void;
}

export async function sendChatMessageStream(
  message: string,
  threadId: string | undefined,
  callbacks: ChatStreamCallbacks,
  signal?: AbortSignal,
  csContextSeed?: Record<string, unknown>
): Promise<void> {
  const baseUrl = client.defaults.baseURL || '';
  const url = `${baseUrl}/chat/send/stream`;

  // Send `cs_context_seed` only when seeding a new thread; the backend
  // ignores it on existing threads (the seed already lives in their history).
  const body: Record<string, unknown> = { message };
  if (threadId) {
    body.thread_id = threadId;
  }
  if (csContextSeed && !threadId) {
    body.cs_context_seed = csContextSeed;
  }

  // Get auth token (same key used by axios client interceptor)
  const token = localStorage.getItem('product_dashboard_token');

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  if (!response.body) {
    throw new Error('No response body');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Process complete SSE messages
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const jsonStr = line.slice(6);
          if (jsonStr.trim()) {
            try {
              const event = JSON.parse(jsonStr) as SSEEvent;
              handleSSEEvent(event, callbacks);
            } catch (e) {
              console.error('Failed to parse SSE event:', jsonStr, e);
            }
          }
        }
      }
    }

    // Process any remaining data in buffer
    if (buffer.startsWith('data: ')) {
      const jsonStr = buffer.slice(6);
      if (jsonStr.trim()) {
        try {
          const event = JSON.parse(jsonStr) as SSEEvent;
          handleSSEEvent(event, callbacks);
        } catch (e) {
          console.error('Failed to parse final SSE event:', jsonStr, e);
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

function handleSSEEvent(event: SSEEvent, callbacks: ChatStreamCallbacks): void {
  const data = event.data || {};

  switch (event.type) {
    case 'connected':
      callbacks.onConnected?.(data.thread_id as string, data.is_new_thread as boolean);
      break;
    case 'status':
      callbacks.onStatus?.(data.message as string);
      break;
    case 'tool_start':
      callbacks.onToolStart?.(data.tool as string, data.input as Record<string, unknown>);
      break;
    case 'tool_result': {
      const chartData = data.chart as Record<string, unknown> | undefined;
      const toolResultData: ToolResultData = {
        rowCount: data.row_count as number,
        columns: data.columns as string[],
        executionTimeMs: data.execution_time_ms as number,
        truncated: data.truncated as boolean,
        rows: data.rows as Record<string, unknown>[] | undefined,
        chart: chartData ? {
          chartType: chartData.chartType as ChartConfig['chartType'],
          xAxis: chartData.xAxis as string,
          yAxes: chartData.yAxes as string[],
          title: chartData.title as string | undefined,
          data: data.rows as Record<string, unknown>[],
        } : undefined,
      };
      callbacks.onToolResult?.(data.tool as string, toolResultData);
      break;
    }
    case 'tool_error':
      callbacks.onToolError?.(data.tool as string, data.error as string);
      break;
    case 'text_delta':
      callbacks.onTextDelta?.(data.content as string);
      break;
    case 'message_saved':
      callbacks.onMessageSaved?.(data.message_id as string, data.role as string);
      break;
    case 'thread_title':
      callbacks.onThreadTitle?.(data.title as string);
      break;
    case 'done':
      callbacks.onDone?.(
        data.thread_id as string,
        data.user_message_id as string,
        data.assistant_message_id as string,
        data.tool_message_ids as string[]
      );
      break;
    case 'error':
      callbacks.onError?.(data.error as string, data.recoverable as boolean);
      break;
    default:
      console.warn('Unknown SSE event type:', event.type);
  }
}

// ---------------------------------------------------------------------------
// Agent monitoring
// ---------------------------------------------------------------------------

function buildAgentParams(filters: AgentFilters): Record<string, string | string[]> {
  const params: Record<string, string | string[]> = {};
  if (filters.startDate) params.start_date = filters.startDate.toISOString();
  if (filters.endDate) params.end_date = filters.endDate.toISOString();
  if (filters.surfaces && filters.surfaces.length > 0) params.surfaces = filters.surfaces;
  if (filters.agentId) params.agent_id = filters.agentId;
  if (filters.orgIds && filters.orgIds.length > 0) params.org_ids = filters.orgIds;
  if (filters.userIds && filters.userIds.length > 0) params.user_ids = filters.userIds;
  if (filters.excludeOrgIds && filters.excludeOrgIds.length > 0)
    params.exclude_org_ids = filters.excludeOrgIds;
  if (filters.excludeUserIds && filters.excludeUserIds.length > 0)
    params.exclude_user_ids = filters.excludeUserIds;
  if (filters.search) params.search = filters.search;
  return params;
}

export async function getAgentNames(): Promise<string[]> {
  const response = await client.get('/agents/names');
  return response.data as string[];
}

export async function getAgentSummary(
  filters: AgentFilters = {},
  bucket: 'hour' | 'day' | 'week' | 'month' = 'day'
): Promise<AgentSummary> {
  const params = { ...buildAgentParams(filters), bucket };
  const response = await client.get('/agents/summary', { params });
  return transformKeys(response.data) as AgentSummary;
}

export async function getAgentConversations(
  filters: AgentFilters = {},
  page: number = 1,
  pageSize: number = 50
): Promise<AgentConversationListResponse> {
  const params = {
    ...buildAgentParams(filters),
    page: page.toString(),
    page_size: pageSize.toString(),
  };
  const response = await client.get('/agents/conversations', { params });
  return transformKeys(response.data) as AgentConversationListResponse;
}

export async function getAgentConversation(threadId: string): Promise<AgentConversationDetail> {
  const response = await client.get(`/agents/conversations/${encodeURIComponent(threadId)}`);
  return transformKeys(response.data) as AgentConversationDetail;
}
